import React from 'react';
import { Filter } from '../common';
import { BarChartOutlined, DeveloperModeOutlined, EventOutlined } from '@mui/icons-material';
import { LiveEventTypeFilter, LiveEventStatusFilter } from '../../enums';
import { Checkbox } from '@mui/material';
import { FilterList, FilterListItem, FilterListItemIcon, FilterListItemText } from '../../utils';
import { toNumber } from 'lodash';

interface Props {
    readonly styled: StyledProps;
    readonly developerOnly: boolean;
    readonly typeFilter: LiveEventTypeFilter;
    readonly statusFilter: LiveEventStatusFilter;
    readonly onFilter: (typeFilter: LiveEventTypeFilter, statusFilter: LiveEventStatusFilter, developerOnly: boolean) => void;
}

interface State {
    readonly typeFilter: LiveEventTypeFilter;
    readonly statusFilter: LiveEventStatusFilter;
    readonly developerOnly: boolean;
}

export class LiveEventsFilter extends React.Component<Props, State> {
    state: State = {
        typeFilter: LiveEventTypeFilter.All,
        statusFilter: LiveEventStatusFilter.All,
        developerOnly: false,
    };

    componentDidUpdate(prevProps: Props) {
        const { developerOnly, typeFilter, statusFilter } = this.props;
        if ( prevProps.developerOnly === developerOnly && prevProps.typeFilter === typeFilter && prevProps.statusFilter === statusFilter ) {
            return;
        }

        this.setState({
            typeFilter,
            statusFilter,
            developerOnly,
        });
    }

    render() {
        const { themeMode } = this.props.styled;
        const { typeFilter, statusFilter, developerOnly } = this.state;

        const onFilterDeveloper = () => this.setState({ developerOnly: !developerOnly });
        const onFilter = () => {
            this.props.onFilter(typeFilter, statusFilter, developerOnly);
        };

        const onClose = () => this.setState({
            typeFilter: this.props.typeFilter,
            statusFilter: this.props.statusFilter,
            developerOnly: this.props.developerOnly,
        });

        return (
            <Filter
                styled={this.props.styled}
                title={'Filter Live Events'}
                menuTitles = {['Event Type', 'Status', 'Player Type']}
                menuElements = {[
                    (
                        <FilterList styled={this.props.styled}>
                            {Object.keys(LiveEventTypeFilter)
                                .filter((key) => Number.isNaN(+key))
                                .map((key, value) => {
                                    if (value === LiveEventTypeFilter.All) {
                                        return <></>;
                                    }
                                    const handleTypeFilterChange = () => this.setState({ typeFilter: value });

                                    return (
                                        <FilterListItem
                                            styled={this.props.styled}
                                            key={`live_event_type_filter_${value}`}
                                            secondaryAction={
                                                <Checkbox
                                                    edge="end"
                                                    onChange={handleTypeFilterChange}
                                                    checked={typeFilter === value}
                                                />
                                            }
                                        >
                                            <FilterListItemIcon styled={this.props.styled}>
                                                {value === LiveEventTypeFilter.Regular ? <EventOutlined style={{ width: 18, height: 18 }} /> : <BarChartOutlined style={{ width: 18, height: 18 }} />}
                                            </FilterListItemIcon>
                                            <FilterListItemText text={key} />
                                        </FilterListItem>
                                    );
                                }
                            )}
                        </FilterList>
                    ),
                    (
                        <FilterList styled={this.props.styled}>
                            {Object.keys(LiveEventStatusFilter)
                                .filter((key) => Number.isNaN(+key))
                                .map((key, index) => {
                                    if (index === LiveEventStatusFilter.All) {
                                        return;
                                    }
                                    const handleStatusFilterChange = () => this.setState({ statusFilter: index });

                                    let text = '';
                                    let color = '';
                                    switch (index) {
                                        case LiveEventStatusFilter.Active:
                                            text = 'In Progress';
                                            color = 'green';
                                            break;
                                        case LiveEventStatusFilter.Coming:
                                            text = 'Scheduled';
                                            color = 'grey';
                                            break;
                                        case LiveEventStatusFilter.Completed:
                                            text = 'Completed';
                                            color = 'orange';
                                            break;
                                    }

                                    return (
                                        <FilterListItem
                                            styled={this.props.styled}
                                            key={`live_event_status_filter_${index}`}
                                            secondaryAction={
                                                <Checkbox
                                                    edge="end"
                                                    onChange={handleStatusFilterChange}
                                                    checked={statusFilter === index}
                                                />
                                            }
                                        >
                                            <FilterListItemText text={text} color={color} />
                                        </FilterListItem>
                                    );
                                }
                            )}
                        </FilterList>
                    ),
                    (
                        <FilterList styled={this.props.styled}>
                            <FilterListItem
                                styled={this.props.styled}
                                key={`developer_only`}
                                secondaryAction={
                                    <Checkbox
                                        edge="end"
                                        onChange={onFilterDeveloper}
                                        checked={developerOnly}
                                        style={{ color: 'inherit' }}
                                    />
                                }
                            >
                                <FilterListItemIcon styled={this.props.styled}>
                                    <DeveloperModeOutlined style={{ width: 18, height: 18, color: '#21BA47' }} />
                                </FilterListItemIcon>
                                <FilterListItemText text={'Developers Only'} />
                            </FilterListItem>
                        </FilterList>
                    ),
                ]}
                onFilter={onFilter}
                onClose={onClose}
            />
        );
    }
}
